// UX workspace: case-study menu on the left (1/4), the selected case study on the right (3/4).
// Stacks on compact screens (menu above content). Selection lives in the hash: #/portfolio/ux/<slug>.
(function () {
  const { Box, Typography, ButtonBase } = MaterialUI;
  const { useRef, useEffect, useState } = React;

  // Page wording per template (the reader here and StudyEditor, which loads later in the editor bundle, both use it).
  window.StudyPageProps = t => (t === 'ux' ? {} : t === 'blank' ? { label: '', backLabel: 'Back' } : t === 'home' ? { bare: true } : { label: t === 'coding' ? 'Coding blog' : 'Blog', backLabel: 'Posts', showDate: true });

  // Admins (canEdit) also get: new case study, edit, reorder, draft badges. `reload(selectSlug?)` refetches from the API.
  function UxWorkspace({ studies = [], slug, onSelect, canEdit = false, reload, section = 'ux', title = 'UX case studies', template = 'ux' }) {
    const { CaseStudyPage, PortfolioEmbedContext, StudyEditor, BlogFinder, Button, IconButton, TextField, Chip, Banner, GoogleIcon, Tooltip } = window.Components;
    const api = window.PortfolioApi;
    const [editing, setEditing] = useState(false);
    const [sideSlot, setSideSlot] = useState(null); // left-column host for the editor's "Add block" list
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [error, setError] = useState('');
    const blank = template === 'blank' || template === 'home'; // one page, no side menu
    const noun = template === 'ux' ? 'case study' : blank ? 'page' : 'post';
    const blog = template === 'blog'; // blog sections get search, sort and a date range instead of a hand-ordered list
    const [finder, setFinder] = useState(window.BlogFinder.defaultState);
    const visible = blog ? window.BlogFinder.apply(studies, finder) : studies;
    const current = studies.find(s => s.slug === slug) || visible[0] || studies[0];
    const pageProps = window.StudyPageProps(template);
    const shortDate = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');
    const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';
    const rootRef = useRef(null);
    const prevSlug = useRef(current && current.slug);

    // Picking another case study scrolls back to the start of the workspace (just under the toolbar).
    useEffect(() => {
      if (!current || prevSlug.current === current.slug) return;
      prevSlug.current = current.slug;
      const reduce = (window.M3Core ? window.M3Core.reducedMotion() : window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      if (rootRef.current) rootRef.current.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    }, [current && current.slug]);

    // Leave edit mode when another study is shown.
    useEffect(() => { setEditing(false); }, [current && current.slug]);

    const create = async () => {
      if (!newTitle.trim()) return;
      try {
        const created = await api.create(newTitle.trim(), section);
        setNewTitle(''); setCreating(false); setError('');
        await reload(created.slug);
        setEditing(true);
      } catch (e) { setError(e.message); }
    };
    const moveTo = async (pos) => {
      const slugs = studies.map(s => s.slug);
      const i = slugs.indexOf(current.slug);
      const j = Math.min(slugs.length, Math.max(1, pos)) - 1;
      if (j === i) return;
      slugs.splice(j, 0, slugs.splice(i, 1)[0]);
      try { await api.order(slugs); await reload(); } catch (e) { setError(e.message); }
    };

    const newForm = canEdit && (
      <Box sx={{ px: 1, pb: 1 }}>
        {creating ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField size="small" label="Title" value={newTitle} onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') create(); }} autoFocus />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="filled" disabled={!newTitle.trim()} onClick={create}>Create</Button>
              <Button size="small" variant="text" onClick={() => { setCreating(false); setNewTitle(''); }}>Cancel</Button>
            </Box>
          </Box>
        ) : (
          <Button size="small" variant="tonal" icon={<GoogleIcon name="add" size={18} />} disabled={editing} onClick={() => setCreating(true)}>New {noun}</Button>
        )}
      </Box>
    );

    if (!current && blank) {
      return canEdit ? (
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>This page is empty.</Typography>
          <Button variant="filled" icon={<GoogleIcon name="add" size={18} />} onClick={async () => {
            try { const created = await api.create(title, section); await reload(created.slug); setEditing(true); } catch (e) { setError(e.message); }
          }}>Create page</Button>
          {error && <Banner severity="error" onClose={() => setError('')} sx={{ mt: 2 }}>{error}</Banner>}
        </Box>
      ) : null;
    }
    if (!current) {
      return (
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>No {noun}s yet.</Typography>
          {newForm}
          {error && <Banner severity="error" onClose={() => setError('')}>{error}</Banner>}
        </Box>
      );
    }

    return (
      <Box ref={rootRef} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: { xs: 2, md: 3 }, scrollMarginTop: 84 }}>
        {(!blank || editing) && <Box
          component="nav"
          aria-label={title}
          sx={{
            flex: { md: '0 0 25%' },
            width: { xs: '100%', md: 'auto' },
            minWidth: 0,
            position: { md: 'sticky' },
            top: { md: 84 },
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          {/* While editing, the page list gives way to the editor's block list in this column */}
          {!editing && <>
            {blog && <BlogFinder state={finder} onChange={setFinder} total={studies.length} shown={visible.length} />}
            {blog && visible.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 1 }}>No posts match.</Typography>}
            <window.Components.StudyList
              label={title}
              disabled={editing}
              onSelect={onSelect}
              items={visible.map(s => ({ key: s.slug, title: s.title.split(' —')[0], selected: s.slug === current.slug, supporting: [template === 'ux' ? s.kind : shortDate(window.BlogFinder.dayOf(s)), s.published === false ? 'Draft' : ''].filter(Boolean).join(' · ') }))}
            />
            {newForm}
          </>}
          {editing && <Box ref={setSideSlot} />}
        </Box>}

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {error && <Banner severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Banner>}
          {canEdit && editing ? (
            <StudyEditor
              key={current.slug}
              template={template}
              sideSlot={sideSlot}
              study={current}
              onSaved={() => reload()}
              onCancel={() => setEditing(false)}
              onDeleted={async () => { setEditing(false); await reload(); onSelect(); }}
            />
          ) : (
            <>
              {canEdit && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                  <Button variant="tonal" size="small" icon={<GoogleIcon name="edit" size={18} />} onClick={() => setEditing(true)}>Edit {noun}</Button>
                  {!blog && !blank && <window.Components.PositionField value={studies.indexOf(current) + 1} max={studies.length} onMove={moveTo} label="Menu position" />}
                  {!blog && !blank && <Tooltip title="Move up in the menu"><span><IconButton size="small" aria-label="Move up in the menu" disabled={studies[0] === current} onClick={() => moveTo(studies.indexOf(current))}><GoogleIcon name="arrow_upward" size={18} /></IconButton></span></Tooltip>}
                  {!blog && !blank && <Tooltip title="Move down in the menu"><span><IconButton size="small" aria-label="Move down in the menu" disabled={studies[studies.length - 1] === current} onClick={() => moveTo(studies.indexOf(current) + 2)}><GoogleIcon name="arrow_downward" size={18} /></IconButton></span></Tooltip>}
                  {current.published === false && <Chip size="small" label="Draft: only visible to you" />}
                </Box>
              )}
              <PortfolioEmbedContext.Provider value="workspace">
                <CaseStudyPage key={current.slug} study={current} onBack={() => {}} {...pageProps} />
              </PortfolioEmbedContext.Provider>
            </>
          )}
        </Box>
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.UxWorkspace = UxWorkspace;
})();
